import { useState } from "react";
import { X, Check } from "lucide-react";
import { rootDomain } from "../../lib/subdomain";
import { cn } from "@nthamizh/ui";
import { useToast } from "@nthamizh/ui";
import { useCreateEnterprise } from "@platform/hooks";

const MODULES = [
  { key: "design", name: "ConfigIQ", description: "HCM implementation accelerator" },
  { key: "analytics", name: "Analytics", description: "Reporting and insights" },
];

interface NewEnterpriseModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NewEnterpriseModal({ isOpen, onClose }: NewEnterpriseModalProps) {
  const createEnterprise = useCreateEnterprise();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", slug: "", adminEmail: "" });
  const [moduleKeys, setModuleKeys] = useState<string[]>(["design"]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (!isOpen) return null;

  const slugify = (v: string) =>
    v.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

  const handleNameChange = (name: string) => {
    setForm((f) => ({ ...f, name, slug: f.slug || slugify(name) }));
  };

  const toggleModule = (key: string) =>
    setModuleKeys((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = "Enterprise name is required.";
    if (!form.slug.trim()) e.slug = "Subdomain is required.";
    if (!/^[a-z0-9-]+$/.test(form.slug)) e.slug = "Lowercase letters, numbers and hyphens only.";
    if (!form.adminEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.adminEmail))
      e.adminEmail = "Enter a valid admin email.";
    if (moduleKeys.length === 0) e.modules = "Select at least one module.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      await createEnterprise.mutateAsync({ ...form, moduleKeys });
      toast({ title: `${form.name} created`, variant: "success" });
      setForm({ name: "", slug: "", adminEmail: "" });
      setModuleKeys(["design"]);
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to create enterprise";
      toast({ title: msg, variant: "error" });
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-ds-border">
            <h2 className="text-sm font-semibold text-ds-text">New enterprise</h2>
            <button onClick={onClose} className="p-1 rounded hover:bg-ds-bg text-ds-text-muted">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Enterprise name</label>
              <input
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Acme Inc"
                className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
              />
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Subdomain</label>
              <div className="flex">
                <input
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({ ...f, slug: slugify(e.target.value) }))}
                  placeholder="acme"
                  className="flex-1 px-3 py-2 text-sm border border-ds-border rounded-l-lg focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
                />
                <span className="px-3 py-2 text-sm text-ds-text-muted border border-l-0 border-ds-border rounded-r-lg bg-ds-bg">
                  .{rootDomain()}
                </span>
              </div>
              {errors.slug && <p className="text-xs text-red-500 mt-1">{errors.slug}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Enterprise admin email</label>
              <input
                type="email"
                value={form.adminEmail}
                onChange={(e) => setForm((f) => ({ ...f, adminEmail: e.target.value }))}
                placeholder="admin@acme.com"
                className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
              />
              {errors.adminEmail && <p className="text-xs text-red-500 mt-1">{errors.adminEmail}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-ds-text-secondary mb-2">Modules to enable</label>
              <div className="space-y-1.5">
                {MODULES.map((m) => (
                  <button
                    key={m.key}
                    onClick={() => toggleModule(m.key)}
                    className={cn(
                      "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border text-left text-xs transition-colors",
                      moduleKeys.includes(m.key) ? "border-indigo-300 bg-indigo-50" : "border-ds-border hover:bg-ds-bg"
                    )}
                  >
                    <div className={cn("w-4 h-4 rounded border flex items-center justify-center flex-shrink-0",
                      moduleKeys.includes(m.key) ? "bg-indigo-600 border-indigo-600" : "border-ds-border-strong")}>
                      {moduleKeys.includes(m.key) && <Check className="w-2.5 h-2.5 text-white" />}
                    </div>
                    <div>
                      <p className="font-medium text-ds-text">{m.name}</p>
                      <p className="text-ds-text-muted">{m.description}</p>
                    </div>
                  </button>
                ))}
              </div>
              {errors.modules && <p className="text-xs text-red-500 mt-1">{errors.modules}</p>}
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-2 justify-end px-5 py-4 border-t border-ds-border">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-ds-border text-sm text-ds-text-secondary hover:bg-ds-bg">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={createEnterprise.isPending}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            >
              {createEnterprise.isPending ? "Creating..." : "Create enterprise"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
